import React from 'react';
import propTypes from 'prop-types';
import { Button, Columns, Column, ModalCardBody, ModalCardHeader, ModalCardTitle, ModalCard, ModalBackground, Modal, Control, Field as BulmaField } from 'src/ui/mui-ui-kit';
import { InnerLoader } from 'src/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';

export function AuditHistoryModal({ isActive, onClose, history, loading }) {
  if (!isActive) {
    return null;
  }

  const getFieldLabel = (fieldName) => {
    const labels = {
      'next_audit_date': 'Next Audit Date',
      'last_audit_date': 'Last Audit Date',
      'status': 'Status',
      'notes': 'Notes',
      'created': 'Created',
    };
    return labels[fieldName] || fieldName;
  };

  const formatValue = (fieldName, value) => {
    if (!value) return 'N/A';
    
    // Format dates
    if (fieldName === 'next_audit_date' || fieldName === 'last_audit_date') {
      return moment(value).format('MM/DD/YYYY');
    }
    
    // Format status
    if (fieldName === 'status') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    
    // For notes, show a preview if too long
    if (fieldName === 'notes' && value.length > 100) {
      return value.substring(0, 100) + '...';
    }
    
    return value;
  };

  return (
    <Modal isActive={isActive} className="audit-history-modal">
      <ModalBackground onClick={onClose} />
      <ModalCard>
        <InnerLoader visible={loading} />
        <ModalCardHeader>
          <ModalCardTitle
            tag="h5"
            className="has-text-weight-semibold columns is-vcentered"
          >
            <Column isSize="narrow" tag="span">
              <span className="icon is-medium has-icon-gradient-bright">
                <FontAwesomeIcon icon={['fal', 'history']} size="lg" />
              </span>
            </Column>
            <Column tag="span" hasTextColor="primary" isPaddingless>
              Audit History
            </Column>
          </ModalCardTitle>
        </ModalCardHeader>
        <ModalCardBody>
          {history && history.length > 0 ? (
            <div className="audit-history-list">
              {history.map((entry, index) => (
                <div key={index} className="box" style={{ marginBottom: '1rem' }}>
                  <div className="columns is-vcentered is-mobile">
                    <div className="column">
                      <p className="heading">Field Changed</p>
                      <p className="title is-6">{getFieldLabel(entry.field_changed)}</p>
                    </div>
                    <div className="column">
                      <p className="heading">Changed By</p>
                      <p className="title is-6">{entry.changed_by_name || 'Unknown'}</p>
                    </div>
                    <div className="column">
                      <p className="heading">Date</p>
                      <p className="title is-6">
                        {moment(entry.changed_date).format('MM/DD/YYYY h:mm A')}
                      </p>
                    </div>
                  </div>
                  {entry.field_changed !== 'created' && (
                    <div className="columns is-mobile">
                      <div className="column">
                        <p className="heading">Old Value</p>
                        <p className="subtitle is-6">
                          {formatValue(entry.field_changed, entry.old_value)}
                        </p>
                      </div>
                      <div className="column">
                        <p className="heading">New Value</p>
                        <p className="subtitle is-6">
                          {formatValue(entry.field_changed, entry.new_value)}
                        </p>
                      </div>
                    </div>
                  )}
                  {entry.field_changed === 'created' && (
                    <div className="columns is-mobile">
                      <div className="column">
                        <p className="subtitle is-6">
                          {entry.new_value}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="notification">
              No history available for this audit.
            </div>
          )}
          <BulmaField isGrouped="centered">
            <Control>
              <Button hasTextColor="info" onClick={onClose}>
                Close
              </Button>
            </Control>
          </BulmaField>
        </ModalCardBody>
      </ModalCard>
    </Modal>
  );
}

AuditHistoryModal.propTypes = {
  isActive: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
  history: propTypes.array,
  loading: propTypes.bool,
};

AuditHistoryModal.defaultProps = {
  history: [],
  loading: false,
};
